import * as React from 'react';

import user from 'js/lib/user';

import type { Program, ThirdPartyOrganization } from 'bundles/enroll-common/common/Enterprise';
import { getAvailableInvitedPrograms, getAvailablePrograms } from 'bundles/enroll-common/lib/apiClient';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import EnterpriseAvailableEnrollmentProgramsContext from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseAvailableEnrollmentProgramsContext';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseSpecializationPageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffSpecializationContext';

type Props = {
  children: React.ReactNode;
};

const EnterpriseAvailableEnrollmentProgramsDataProvider = ({ children }: Props) => {
  // current program/third party org/membership are all for the current program whose slug is in the URL.
  const currentProgram = useProgram();
  const currentThirdPartyOrganization = useThirdPartyOrganization();
  const currentProgramMembership = useProgramMembership();
  const coursePageData = useEnterpriseCoursePageData();
  const specializationPageData = useEnterpriseSpecializationPageData();
  const [availablePrograms, setAvailablePrograms] = React.useState<Array<Program>>();
  const [invitedAvailablePrograms, setInvitedAvailablePrograms] = React.useState<Array<Program>>();
  const [availableThirdPartyOrganizations, setAvailableThirdPartyOrganizations] =
    React.useState<Array<ThirdPartyOrganization>>();
  const [invitedAvailableThirdPartyOrganizations, setInvitedAvailableThirdPartyOrganizations] =
    React.useState<Array<ThirdPartyOrganization>>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasAttemptedQuery, setHasAttemptedQuery] = React.useState(false);

  React.useEffect(() => {
    if (
      user.isAuthenticatedUser() &&
      !currentProgramMembership?.isMembershipLoading &&
      !currentProgramMembership?.isProgramMember &&
      !hasAttemptedQuery &&
      (specializationPageData?.id || coursePageData?.id)
    ) {
      const options = specializationPageData?.id
        ? { s12nId: specializationPageData.id as string }
        : { courseId: coursePageData?.id as string };
      const fetchData = async () => {
        setIsLoading(true);
        setHasAttemptedQuery(true);

        try {
          const [{ programs, thirdPartyOrganizations }, { invitedPrograms, invitedThirdPartyOrganizations }] =
            await Promise.all([getAvailablePrograms(options), getAvailableInvitedPrograms(options)]);

          // We only want to keep programs that are in the same org as the "currentProgram" (whose program slug is in the URL).
          setAvailablePrograms(
            programs?.filter(
              (program) => program.thirdPartyOrganizationId === currentProgram?.naptimeThirdPartyOrganization?.id
            )
          );
          setInvitedAvailablePrograms(
            invitedPrograms?.filter(
              (program) => program.thirdPartyOrganizationId === currentProgram?.naptimeThirdPartyOrganization?.id
            )
          );
          // We only want to keep the third party org that is the same org that the "currentProgram" (whose program slug is in the URL) is in.
          // We don't necessarily need this data, but simpler for now to attach it to this context (we get this from call to get program info).
          setAvailableThirdPartyOrganizations(
            thirdPartyOrganizations?.filter((org) => org.id === currentThirdPartyOrganization?.id)
          );
          setInvitedAvailableThirdPartyOrganizations(
            invitedThirdPartyOrganizations?.filter((org) => org.id === currentThirdPartyOrganization?.id)
          );
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          console.error(error);
        }
      };

      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProgramMembership?.isMembershipLoading,
    currentProgramMembership?.isProgramMember,
    coursePageData?.id,
    specializationPageData?.id,
  ]);

  return (
    <EnterpriseAvailableEnrollmentProgramsContext.Provider
      value={{
        programs: availablePrograms,
        invitedPrograms: invitedAvailablePrograms,
        thirdPartyOrganizations: availableThirdPartyOrganizations,
        invitedThirdPartyOrganizations: invitedAvailableThirdPartyOrganizations,
        // To prevent the program deny modal from showing on SSR before we make our queries, make loading true on SSR for authenticated users.
        isLoading: typeof window === 'undefined' && user.isAuthenticatedUser() ? true : isLoading,
      }}
    >
      {children}
    </EnterpriseAvailableEnrollmentProgramsContext.Provider>
  );
};

export default EnterpriseAvailableEnrollmentProgramsDataProvider;
